import React, { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { H2, H3 } from '@piconetworks/pkg-heading'
import ToggleBar from '@piconetworks/pkg-toggle-bar'
import { format } from '@piconetworks/pkg-currency'
import Boutton from '@piconetworks/pkg-boutton'

import { useFeatureManager } from 'features/FeatureManager'
import useUserId from 'hooks/useUserId'
import usePublisherId from 'hooks/usePublisherId'
import useUserCount from 'hooks/useUserCount'

import MessagesModule from 'modules/messages'
import AccountModule from 'modules/account'

import { calculateHypePluginPricing } from 'utils/lib/pricing'

import AddonPurchaser from './IntegrationPurchaser'

import styles from './PlanDetail.module.scss'

const PlanItem = ({
    creditAmount = '',
    creditType = '',
    tierName = '',
    pricingInterval = '',
    pricingAmount = '',
    pricingWordingOverride = null,
}) => {

    const formattedCreditAmount = Intl.NumberFormat('en-US').format(Number(creditAmount))
    const abbrIntervalMap = {
        'month': 'mo',
        'year': 'yr',
    }
    const intervalAbbr = abbrIntervalMap[pricingInterval]

    return (
        <div className={styles.planItem}>
            <div className={styles.planItemInfo}>
                <H3>{tierName}</H3>
                {creditAmount && (<div className={styles.credits}>{formattedCreditAmount} {creditType} credits per month</div>)}
            </div>
            <div className={styles.price}>
                {pricingWordingOverride ? pricingWordingOverride : pricingAmount}
                {pricingWordingOverride === "FREE" ? '' : <span> /{intervalAbbr}</span>}
            </div>
        </div>
    )
}

const PlanDetail = ({
    onChange = (e = null) => {},
    intervals = [],
    defaultInterval = null,
}) => {
    // if (intervals?.length === 0) {
    //     return null
    // }

    // const intervals = ['month']

    return (
        <ToggleBar.Group
            name='planDetail'
            onChange={onChange}
            className={styles.interval}
            defaultValue={defaultInterval}
            variant='primary'
        >
            {['month'].reduce((acc, interval) => {
                const properIntervalMap = {
                    'month': 'Monthly',
                    'year': 'Annual',
                }

                const intervalProper = properIntervalMap[interval]

                if (!intervalProper) {
                    return acc
                }

                acc.push(
                    <ToggleBar.Item key={interval} value={interval}>{intervalProper}</ToggleBar.Item>
                )

                return acc
            }, [])}
        </ToggleBar.Group>
    )
}

const PlanPicker = ({
    onChange = (e = null) => {},
    plans = [],
    defaultPricing = null,
}) => {
    const sortedPlans = plans.sort(({ pricingAmount: a } = {}, { pricingAmount: b } = {}) => a - b)

    return (
        <ToggleBar.Group
            className={styles.planPicker}
            defaultValue={defaultPricing}
            isVertical
            key={`_${defaultPricing}`}
            name='planPicker'
            onChange={onChange}
            variant={"primary"}
        >
            {sortedPlans?.reduce((acc, plan) => {
                const pricingId = plan.pricingId

                if (!pricingId) {
                    return acc
                }

                const pricingAmount = format({ amount: plan.pricingAmount, currency: plan.pricingCurrency, symbol: true })

                acc.push(
                    <ToggleBar.Item key={plan.pricingId} value={plan.pricingId}>
                        <PlanItem
                            creditAmount={plan.creditAmount}
                            creditType={plan.creditType}
                            tierName={plan.tierName}
                            pricingInterval={plan.pricingInterval}
                            pricingAmount={pricingAmount}
                            pricingWordingOverride={plan.pricingWordingOverride}
                        />
                    </ToggleBar.Item>
                )

                return acc
            }, [])}
        </ToggleBar.Group>
    )
}

// a Store page with a CTA that when clicked triggers the Cart
const PlanListing = ({ appLabel }) => {
    const dispatch = useDispatch()
    const [currentInterval, setInterval] = useState(null)
    const [selectedPricingId, selectPricingId] = useState(null)

    const userId = useUserId()
    const publisherId = usePublisherId()
    const userCount = useUserCount()

    const { getAppByLabel } = useFeatureManager({
        userId,
        publisherId,
    })

    const app = getAppByLabel(appLabel)

    const tiers = app?.tiers || []

    const intervals = Object.keys(tiers?.reduce((acc, tier) => {
        tier?.pricings?.forEach((pricing) => {
            const interval = pricing?.interval

            if (interval) {
                acc[interval] = null
            }
        })


        return acc
    }, {})).sort()

    const defaultInterval = intervals?.[0]

    const planItems = tiers?.reduce((acc, tier) => {
        let pricing;
        const planItem = {
            tierName: '',
            creditAmount: 0,
            creditType: '',
            pricingInterval: '',
            pricingAmount: 0,
            pricingId: '',
            pricingCurrency: '',
            pricingWordingOverride: null,
        }

        planItem.tierName = tier?.name

        let pricingWordingOverride

        if (appLabel === 'hype-plugin') {
            const monthProductPricing = tier?.pricings?.find(({ interval = null } = {}) => interval === 'month')
            const hypePluginPrice = calculateHypePluginPricing(userCount)

            const price = format({
                amount: hypePluginPrice * 100,
                currencyCode: monthProductPricing?.currency,
                symbol: true,
            })
            pricingWordingOverride = hypePluginPrice === 0 ? 'FREE' : `${price}`
        }

        const credit = tier?.credits?.[0]
        pricing = tier?.pricings?.find((pricing) => pricing?.interval === (currentInterval || defaultInterval))

        if (appLabel === 'unlimited-pages') {
            pricing = tier?.pricings?.find((pricing) => pricing?.interval === 'month' && pricing.deleted_at === null)
        }

        if (appLabel === 'messages') {
            pricingWordingOverride = pricing?.amount === 0 ? 'FREE' : undefined
        }

        if (pricing) {
            planItem.creditAmount = credit?.amount
            planItem.creditType =  appLabel === 'messages' ? 'messages' : credit?.type
            planItem.pricingAmount = pricing?.amount
            planItem.pricingCurrency = pricing?.currency
            planItem.pricingInterval = pricing?.interval
            planItem.pricingId = pricing?.id
            planItem.pricingWordingOverride = pricingWordingOverride

            acc.push(planItem)
        }

        return acc
    } , [])

    const sortedPlans = planItems?.sort(({ pricingAmount: a = null } = {}, { pricingAmount: b = null } = {}) => a - b)

    const defaultPricing = sortedPlans?.[0]?.pricingId

    const fetchMessagingEnabledInProgress = useSelector(MessagesModule.selectors.fetchMessagingEnabledInProgress)
    const hasPhoneNumber = useSelector(AccountModule.selectors.hasPhoneNumber)

    const enableSMS = useCallback(() => {
        dispatch(MessagesModule.creators.enableMessaging())
    }, [dispatch])

    useEffect(() => {
        selectPricingId?.(defaultPricing)
    }, [defaultPricing, selectPricingId])

    return (
        <section className={styles.planContainer}>
            <div className={styles.header}>
                <H2>Plan</H2>
                <PlanDetail
                    intervals={intervals}
                    onChange={setInterval}
                    defaultInterval={defaultInterval}
                />
            </div>
            <PlanPicker
                plans={planItems}
                onChange={selectPricingId}
                defaultPricing={selectedPricingId}
            />
            <div className={styles.cta}>
                {selectedPricingId?.includes('free-99') && (
                    <Boutton
                        variant='primary'
                        type='button'
                        className={styles.width100}
                        onClick={enableSMS}
                        disabled={fetchMessagingEnabledInProgress || hasPhoneNumber}
                    >
                        {hasPhoneNumber ? 'Installed' : 'Subscribe'}
                    </Boutton>
                )}
                {!selectedPricingId?.includes('free-99') && (
                    <AddonPurchaser
                        pricingId={selectedPricingId}
                    >
                        Subscribe
                    </AddonPurchaser>
                )}
            </div>
        </section>
    )
}
export default PlanListing
